/* global Vue, VueRouter, VueI18n, primevue, calendar, LOCALE, PREFIX, DATA_LAYER  */

import Step1 from './views/Step1'
import Step2 from './views/Step2'
import Step3 from './views/Step3'
import Step4 from './views/Step4'
import Step5 from './views/Step5'
import ReservationSummary from './components/ReservationSummary'
import { useState, setErrorAndStopLoading, startLoading, stopLoading, runAsync } from './components/Notifications'
import BookingError from './components/BookingError'
import '../validation-rules'
import * as Api from '../api'
import AuthService from '../AuthService'
import { PhobsError } from '../classes/Errors'
import { isEmpty } from '../utils'
import { useCart, loadCartFromUrl, loadCartFromJson, selectRatePlanPair, updateBookingParameters, updateBookingNights, setIdentity, updateGuestLimits, clearRateId, setBecomeMember, clearSpecialOffer, modifyGuestNumbersAccordingToPropertyLimits, updateBookingGuests, storeInitialGuests, clearInitialGuests } from '../use-cart'
import BookingMenu from './components/BookingMenu'
import { messagesSpecific } from '../i18n/translations'
import { CreateI18n } from '../i18n/create-i18n'
import { localizeCalendar } from '../utils-common'
import { setCurrency } from '../use-currency-observer'

const i18n = CreateI18n(messagesSpecific)

const routes = [
  { path: '/', redirect: '/step1' },
  { path: '/step1', name: 'step1', component: Step1, meta: { title: 'chooseAccomodation' } },
  { path: '/step2', name: 'step2', component: Step2, meta: { title: 'reservationDetails' } },
  { path: '/step3', name: 'step3', component: Step3, meta: { title: 'personalDetails' }, props: true },
  { path: '/step4', name: 'step4', component: Step4, meta: { title: 'finalConfirmation' } },
  { path: '/step5/:reservationId', name: 'step5', component: Step5, meta: { title: 'thankYou' } },
  { path: '/:pathMatch(.*)', component: { template: '<h1 style="color: red">Page Not Found</h1>' } }
]

const router = VueRouter.createRouter({
  history: VueRouter.createWebHistory(PREFIX),
  routes
})

const BookingApp = Vue.createApp({
  name: 'BookingApp',
  components: { ReservationSummary, BookingMenu, BookingError },
  template: `
<BookingError />
<div class="container">
  <div v-show="stepNumber < 5" class="step-tracker">
    <div class="step-tracker__item">
      <div :class="[stepNumber >= 1 ? 'active' : '']">1</div>
      <span>{{ $t('chooseAccomodation') }}</span>
    </div>
    <div class="step-tracker__item">
      <div :class="[stepNumber >= 2 ? 'active' : '']">2</div>
      <span>{{ $t('reservationDetails') }}</span>
    </div>
    <div class="step-tracker__item">
      <div :class="[stepNumber >= 3 ? 'active' : '']">3</div>
      <span>{{ $t('personalDetails') }}</span>
    </div>
    <div class="step-tracker__item">
      <div :class="[stepNumber >= 4 ? 'active' : '']">4</div>
      <span>{{ $t('finalConfirmation') }}</span>
    </div>
  </div>

  <div class="main-title">
    <button
      v-show="stepNumber > 1 && stepNumber < 5"
      class="main-title__back"
      @click="goToPreviusStep">
      <i class="icon-arrow-left"></i>
      <span>{{ $t('back') }}</span>
    </button>
    <h1 class="no-margin">{{ $t($route.meta.title ?? 'loading') }} </h1>
  </div>
</div>

<BookingMenu
  ref="bookingMenu"
  v-if="authEnded && stepNumber === 1 & showBookingMenu"
  :propertyIds="[bookingMenuPropertyId]"
  :property="property"
  :startDate="cart.startDate.value"
  :nights="cart.nights.value"
  :adults="cart.adults.value"
  :children="cart.children.value.map(({ age }) => ({ age }))"
  :accessCode="cart.accessCode.value"
  :specialOffer="cart.specialOffer"
  :showSpecialOffer="!propertyChanged"
  :showGuestsAdjustmentWarning="showGuestsAdjustmentWarning"
  @updateBookingParams="updateBookingParams"
  @propertyChanged="handlePropertyChanged" />
<div class="booking-app-content">
  <div class="container">

    <div class="grid no-row-gap">
      <div class="step col-12 col-md-8">
        <router-view
          v-if="shouldRenderStep"
          :property="property"
          :extras="extras"
          :priceQuote="priceQuote"
          :propertyAvailability="propertyAvailability"
          :isMobile="isMobile"
          @modifySearch="$refs.bookingMenu.toggleModal('calendar', $event)"
          @modifyGuests="$refs.bookingMenu.toggleModal('guests', $event)"
          @updatePrice="updatePriceAsync"
          @addToCart="addToCart"
          @removeRate="removeRate"
          @closeSummary="isSummaryOpen = false" />
      </div>

      <div class="col-12 col-md-4">
        <ReservationSummary
          v-show="!isEmpty(property) && (!isMobile || (stepNumber !== 3 && stepNumber !== 4))"
          :stepNumber="stepNumber"
          :property="property"
          :priceQuote="priceQuote"
          :propertyAvailability="propertyAvailability"
          :isOpen="isSummaryOpen"
          :isInline="false"
          :canRemoveUnit="stepNumber <= 2"
          :guestSummarySource="stepNumber === 1 ? 'booking-bar' : 'cart'"
          @open="isSummaryOpen = true"
          @close="isSummaryOpen = false">
        </ReservationSummary>
      </div>

      <div v-if="stepNumber === 5" id="step5-buttons" class="step-5__buttons col-12 col-xl-8 hide-on-print"></div>
      <div v-if="stepNumber === 5" id="step5-bottom" class="col-12 negative-cp hide-on-print"></div>
    </div>
  </div>
</div>
  `,
  setup () {
    const { cart } = useCart()
    const { state } = useState()
    const authEnded = Vue.ref(false)

    const filterToRateId = Vue.ref(null)
    const extras = Vue.ref('')

    const property = Vue.reactive({})
    const propertyAvailability = Vue.reactive([])
    const priceQuote = Vue.reactive({})
    const propertyChanged = Vue.ref(false)
    const bookingMenuPropertyId = Vue.ref(null)
    const showBookingMenu = Vue.ref(true)
    const showGuestsAdjustmentWarning = Vue.ref(false)

    function handlePropertyChanged (newProperty) {
      propertyChanged.value = property.phobsId !== newProperty.phobsId
      bookingMenuPropertyId.value = newProperty.phobsId
    }

    const stepNumber = Vue.computed(() => parseInt(router.currentRoute.value.name?.slice(-1)))

    const shouldRenderStep = Vue.computed(() => {
      const isSoftLoadingStep1 = stepNumber.value === 1 && state.processes.value.length === 1 && state.processes.value[0] === 'loadPriceQuoteAsync'
      return cart.hasPropertyId.value && (!state.isLoading.value || isSoftLoadingStep1)
    })

    const isSummaryOpen = Vue.ref(false)
    const isMobile = Vue.ref(true)

    function handleResize () {
      isMobile.value = !window.matchMedia('(min-width: 992px)').matches
    }

    Vue.onMounted(async () => {
      window.addEventListener('resize', handleResize)
      handleResize()
    })

    Vue.onBeforeUnmount(() => {
      window.removeEventListener('resize', handleResize)
    })


    const goToPreviusStep = () =>  {
      if (!stepNumber?.value || stepNumber.value < 2 || stepNumber.value > 4) {
        return
      }
      router.push(`/step${stepNumber.value - 1}`)
    }

    const removeRate = () => {
      clearSpecialOffer()
      runAsync(loadPropertyAvailabilityAsync)
    }

    const restoreGuestsIfUntouched = (bookingParams) => {
      if (cart.adults.value !== bookingParams.adults) {
        return
      }
      if (cart.children.value.length !== bookingParams.children.length) {
        return
      }
      for (let i = 0; i < bookingParams.children.length; i++) {
        if (cart.children.value[i].age !== bookingParams.children[i].age) {
          return
        }
      }

      if (cart.initialGuests.value) {
        bookingParams.adults = cart.initialGuests.value.adults
        bookingParams.children = cart.initialGuests.value.children
        clearInitialGuests()
      }
    }

    const updateBookingParams = async bookingParams => {
      showGuestsAdjustmentWarning.value = false
      if (propertyChanged.value) {
        clearSpecialOffer()
        restoreGuestsIfUntouched(bookingParams)
      }
      clearInitialGuests()
      updateBookingParameters(bookingParams)
      startLoading('loadProperty')
      await loadProperty()
      await runAsync(loadPropertyAvailabilityAsync)
    }

    Vue.watch(() => cart.dirtySignal.value, async () => await runAsync(loadPriceQuoteAsync))

    Vue.watch(stepNumber, step => {
      if (step=== 1) {
        document.body.classList.add('step-1')
      } else {
        document.body.classList.remove('step-1')
      }
      isSummaryOpen.value = false
    }, { immediate: true })

    const loadPropertyAvailabilityAsync = async () => {
      propertyAvailability.splice(0)

      const availabilityParams = {
        ...cart.getSerializedForAvailability(),
      }

      propertyAvailability.push(...await Api.getPropertyAvailabilityAsync(availabilityParams))

      // reset unit availability because response might be empty array
      property.units.forEach(u => {
        u.availableUnits = []
        u.rates = []
      })
      propertyAvailability.forEach(pa => {
        extras.value = pa.extras
        const search = property.units.filter(u => u.id === pa.id)
        if (search.length > 0) {
          search[0].availableUnits = pa.availableUnits
          search[0].rates = pa.rates
          search[0].disclaimer = pa.disclaimer
        }
      })
    }

    const loadPriceQuoteAsync = async () => {
      if (cart.hasRateId.value) {
        try {
          const pq = await Api.getPriceQuoteAsync(cart.getSerializedForQuote())
          Object.assign(priceQuote, pq)
        } catch (e) {
          if (e instanceof PhobsError && e.rawCode === 5) {
            console.warn(e.details)
          } else throw e
        }
      } else {
        Object.assign(priceQuote, { rateId: null })
      }
    }

    const loadSpecialOffer = async () => {
      if (cart.specialOffer.value.ratePlanId) {
        setSpecialOffer(await Api.getSpecialOfferAsync(cart.specialOffer.value.ratePlanId, property))
      }
    }

    const setSpecialOffer = specialOffer => {
      if (!specialOffer) {
        return
      }
      cart.specialOffer.value.title = specialOffer?.title || ''
      cart.specialOffer.value.periods = specialOffer.variation.periods ? specialOffer.variation.periods : [{
        start_date: specialOffer.variation.from,
        end_date: specialOffer.variation.expiryDate,
      }]
      cart.specialOffer.value.days = specialOffer.variation.days ? specialOffer.variation.days : null
    }

    const addToCart = async ({ unitId, ratePair, increaseNumberOfNights, skipRouting = false}) => {
      try {
        if (!unitId) {
          throw new Error("missing unitId")
        }
        if (!ratePair || !ratePair.regular) {
          throw new Error("missing rate or regular rate")
        }
        if (increaseNumberOfNights > 0) {
          updateBookingNights(increaseNumberOfNights)
          await runAsync(loadPropertyAvailabilityAsync)
        }

        const unit = property.units.find(pa => pa.id ===  unitId)
        if (!unit) {
          throw new Error("couldn't find unit")
        }
        selectRatePlanPair(ratePair, unit)
        await loadPriceQuoteAsync()

        if (!skipRouting) {
          router.push(`/step1`)
        }

        const summaryEl = document.querySelector('.reservation-summary');
        summaryEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (err) {
        setErrorAndStopLoading(err)
      }

      DATA_LAYER?.bookingRateSelected({ cart, priceQuote, property })
    }

    const updatePriceAsync = () => runAsync(loadPriceQuoteAsync)

    const loaderSvg = document.getElementById('loaderSvg')
    if (loaderSvg) {
      Vue.watch(state.isLoading, isLoading => {
        if (isLoading) {
          loaderSvg.classList.remove('is-hidden')
        } else {
          loaderSvg.classList.add('is-hidden')
        }
      })
    }

    startLoading('booking-app-mount')

    async function loadProperty () {
      bookingMenuPropertyId.value = cart.propertyId.value
      const newProperty = await Api.getPropertyAsync(cart.propertyId.value, LOCALE)
      Object.assign(property, newProperty)
      stopLoading('loadProperty')

      updateGuestLimits({
        maxAdults: property.maxAdults,
        maxChildren: property.maxChildren,
        maxGuests: property.maxGuests,
        maxChildAge: property.maxChildAge,
      })

      const newGuests = modifyGuestNumbersAccordingToPropertyLimits(property)
      if (newGuests.adults !== cart.adults.value || newGuests.children.length !== cart.children.value.length) {
        showGuestsAdjustmentWarning.value = true
        showBookingMenu.value = false
        storeInitialGuests({  adults: cart.adults.value, children: cart.children.value })
        updateBookingGuests(newGuests)
        Vue.nextTick(() => {
          showBookingMenu.value = true
        })
      }
    }

    // wait for AuthService to be initialized to avoid redirects breaking the flow
    AuthService.on('ended', async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search)
        if (urlParams.has('currency')) {
          setCurrency(urlParams.get('currency'))
        }
        if ('cart' in window.sessionStorage) {
          loadCartFromJson(sessionStorage.getItem('cart'))
        }
        if (!isEmpty(Object.fromEntries(urlParams.entries())) && urlParams.get("propertyId")) {
          loadCartFromUrl(urlParams)
          const ratePlanId = urlParams.get("ratePlanId")
          if (ratePlanId?.length > 1 ) {
            filterToRateId.value = ratePlanId
          }
        }

        await loadProperty()
        await loadSpecialOffer()
        await loadPropertyAvailabilityAsync()

        if (urlParams.get('ratePlan') && urlParams.get('units')) {
          startLoading('addToCartFromUrl')
          const ratePlanId = urlParams.get('ratePlan')
          for (const unitId of urlParams.get('units').split(',')) {
            const ratePair = propertyAvailability
              .find(u => u.id === unitId)?.rates
              .find(rp => rp.member?.id === ratePlanId || rp.regular?.id === ratePlanId)

            await addToCart({ unitId, ratePair, increaseNumberOfNights: 0, skipRouting: true })
          }
          stopLoading('addToCartFromUrl')
        }

        await loadPriceQuoteAsync()

        if (AuthService.isAuthenticated) {
          setIdentity(AuthService.loyaltyId)
        }
        authEnded.value = true
        stopLoading('booking-app-mount')
      } catch (err) {
        setErrorAndStopLoading(err)
      }
      DATA_LAYER?.bookingPageLoad({ stepNumber: stepNumber.value, property, cart })
    })

    AuthService.on('signedIn', () => {
      setBecomeMember(true)
      if (stepNumber.value > 2) {
        router.push('/step2')
      }
    })

    AuthService.on('signingOut', () => {
      clearRateId()
    })

    return {
      cart,
      state,
      authEnded,
      property,
      filterToRateId,
      extras,
      priceQuote,
      stepNumber,
      isSummaryOpen,
      shouldRenderStep,
      propertyAvailability,
      goToPreviusStep,
      updateBookingParams,
      updatePriceAsync,
      isEmpty,
      addToCart,
      removeRate,
      bookingMenuPropertyId,
      handlePropertyChanged,
      showGuestsAdjustmentWarning,
      propertyChanged,
      showBookingMenu,
      isMobile,
    }
  }
})

BookingApp.config.globalProperties.$AuthService = AuthService // same service is importent on all apps
BookingApp.use(primevue, {
  locale: localizeCalendar(LOCALE),
})
BookingApp.use(router)
BookingApp.use(i18n)
BookingApp.mount('#app')

export default BookingApp
